import {
  put, takeLatest, all, select, call, take, fork,
} from 'redux-saga/effects';
import { eventChannel, END } from 'redux-saga';
import { getUser } from 'circe/authentication/selectors';
import { getCurrentAuthHeader } from 'domain/fuse/auth/utils';
import { reset } from 'circe/forms/provider';
import { exception } from 'circe/logger';
import { show as showNotification } from 'circe/notifications/actions';
import * as I18n from 'circe/I18n';
import { bootstrap } from 'circe/actions';
import BroadcastChannel from 'broadcast-channel';
import { authorizedCallback } from 'domain/fuse/payments/actions';
import * as api from './api';
import { pay, paySuccess, payFailed } from './actions';
import { FORM_NAME, PAYMENT_METHODS } from './constants';
import { makeCreditCardApiPayload } from './utils';
import creditCardsSaga from './creditCards/saga';
import creditTermsSaga from './creditTerms/saga';

function* doPayUsingCreditCard({ payload, userData, headers }) {
  try {
    const requestPayload = makeCreditCardApiPayload(payload, userData);
    const results = yield call(api.authorizeCreditCard, requestPayload, headers);

    if (results && results.successful) {
      yield put(
        paySuccess({
          ...requestPayload,
          confirmUrl: results.errorMessage,
        }),
      );
    } else {
      throw new Error('Payment Failed');
    }
  } catch (e) {
    const message = e.response && e.response.data && e.response.data.message
      ? e.response.data.message
      : I18n.str('paymentFailed', 'Unable to process your payment, please try again later.');

    yield put(payFailed(message));

    exception(e);
  }
}

function* doPayUsingCreditTerm({ payload, headers }) {
  try {
    const results = yield call(api.authorizeCreditTerm, payload, headers);

    if (results && results.successful) {
      yield put(
        showNotification(
          I18n.str('paymentCreditTerm', 'Your payment has been confirmed successfully.'),
          'success',
        ),
      );
      yield put(
        paySuccess({
          ...payload,
        }),
      );
    } else {
      throw new Error('Payment Failed');
    }
  } catch (e) {
    const message = e.response && e.response.data && e.response.data.message
      ? e.response.data.message
      : I18n.str('paymentFailed', 'Unable to process your payment, please try again later.');

    yield put(payFailed(message));

    exception(e);
  }
}

function* doPay({ payload }) {
  const state = yield select();
  const userData = yield call(getUser, state);
  const headers = yield call(getCurrentAuthHeader, state);

  const { paymentMethod, ...restPayload } = payload;

  switch (paymentMethod) {
    case PAYMENT_METHODS.CREDIT_TERMS:
      yield doPayUsingCreditTerm({ payload: restPayload, userData, headers });
      break;
    default:
      yield doPayUsingCreditCard({ payload: restPayload, userData, headers });
  }
}

function* doResetPaymentForm() {
  yield put(reset(FORM_NAME));
}

function* doPaymentFailure({ payload }) {
  const { errorMessage } = payload;

  yield put(
    showNotification(
      errorMessage
        || I18n.str(
          'paymentFailed',
          'Please tray again in few minutes, We are unable to process your payment.',
        ),
      'error',
    ),
  );
}

function* doListenToAuthStorage() {
  const storageChannel = eventChannel((emit) => {
    window.addEventListener('storage', (e) => {
      if (e.key === 'MOBILE_AUTHORIZE_PAYMENT_CHANNEL') {
        const responseObj = JSON.parse(e.newValue);

        emit(responseObj);

        localStorage.removeItem('MOBILE_AUTHORIZE_PAYMENT_CHANNEL');
      }
    });

    return () => {};
  });

  try {
    while (true) {
      const { response, message } = yield take(storageChannel);
      yield put(authorizedCallback(response, message.response, message.merchantReference));
    }
  } catch (error) {}
}

function* doListenToAuthBroadcastChannel(authPaymentChannel) {
  const authChannel = eventChannel((emit) => {
    authPaymentChannel.onmessage = function (ev) {
      const responseObj = ev.data ? JSON.parse(ev.data) : JSON.parse(ev);

      emit(responseObj);
    };
    return () => {};
  });

  try {
    while (true) {
      const { response, message } = yield take(authChannel);
      yield put(authorizedCallback(response, message.response, message.merchantReference));
    }
  } catch (error) {}
}

function* doPaymentAuthorization() {
  const authPaymentChannel = new BroadcastChannel('AUTHORIZE_PAYMENT_CHANNEL');

  const isIOSBrowser = navigator.userAgent.match(/iPhone|iPad|iPod/i);

  yield fork(doListenToAuthStorage);

  yield fork(doListenToAuthBroadcastChannel, authPaymentChannel);

  const { search } = window.location;

  if (!search) {
    return;
  }

  const params = new URLSearchParams(search);

  if (params.get('doSpecialPayfort') !== null) {
    return;
  }

  const respCode = params.get('response_code');
  const respMessage = params.get('response_message');
  const merchantReference = params.get('merchant_reference');

  if (!respCode && !respMessage && !merchantReference) {
    // this is not something related to payfort auth
    return;
  }

  const isSuccessCode = parseInt(respCode.toString()) === 2000 || parseInt(respCode.toString()) === 14000;

  const isSuccessMessage = respMessage === 'Success';

  const onFinish = (message) => {
    const response = JSON.stringify(message);

    authPaymentChannel.postMessage(response);

    if (isIOSBrowser) {
      localStorage.setItem('MOBILE_AUTHORIZE_PAYMENT_CHANNEL', response);
    }

    window.location = '/';
    window.close();
  };

  if (isSuccessCode && isSuccessMessage) {
    onFinish({
      response: 'AUTH_SUCCESS',
      message: {
        response: respMessage,
        merchantReference,
      },
    });
  } else if (!isSuccessMessage) {
    onFinish({
      response: 'AUTH_FAILED',
      message: {
        response: respMessage,
        merchantReference,
      },
    });
  }
}

function* watcher() {
  yield all([
    takeLatest(paySuccess().type, doResetPaymentForm),
    takeLatest(pay().type, doPay),
    takeLatest(payFailed().type, doPaymentFailure),
    takeLatest(bootstrap().type, doPaymentAuthorization),
    creditCardsSaga(),
    creditTermsSaga(),
  ]);
}

export default watcher;
